import { Component, ElementRef, HostListener, ViewChild, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-aitools-project-management',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './aitools-project-management.component.html',
  styleUrl: './aitools-project-management.component.scss'
})
export class AitoolsProjectManagementComponent implements OnInit, OnDestroy {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
      .subscribe((event: any) => {
        if (this.router.url === '/blogs/aitools-project-management') {
          this.updateDescription(event.description, event.keywords, event.title);
        }
      });
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const pmToolsSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management",
          "name": "The 7 Best AI Tools for Project Management in 2025 | Kalpita Technologies",
          "description": "Comprehensive review of the 7 best AI tools for project management in 2025 including ClickUp, Trello, Asana, Notion, Wrike, Monday.com, and Clockwise.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog Index" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management", "name": "7 Best AI Tools for PM" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management#blogposting",
          "headline": "The 7 Best AI Tools for Project Management in 2025",
          "articleBody": "In today's fast-paced work environment, AI tools for project management are essential for boosting efficiency...",
          "author": { "@type": "Person", "name": "Grace C" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "@id": "https://www.kalpitatechnologies.com/#organization"
          },
          "datePublished": "2025-04-22",
          "image": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/resources/blog/aitools-projectmanagement/aitools-banner.webp" }
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/aitools-project-management#faqpage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How is AI transforming project management?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AI transforms project management through smarter planning and forecasting, real-time progress tracking, and predictive risk management."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/resources/kalpita-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(pmToolsSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  updateDescription(desc: string, keywords: string, title: string) {
    // this.seoService.updateDescription(desc);
    // this.seoService.updateMetaKeywords(keywords);
    // this.seoService.updateTitle(title);
  }

  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  @ViewChild('tableresponsiveImage') image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 392;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap() {
    if (!this.image?.nativeElement) return;

    const img = this.image.nativeElement;
    const widthRatio = img.clientWidth / this.originalWidth;
    const heightRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll('map[name="table-image-map"] area').forEach((area: any) => {
      let coords = area.getAttribute('data-original-coords') || area.getAttribute('coords');
      if (!coords) return;

      area.setAttribute('data-original-coords', coords);
      const scaledCoords = coords.split(',').map((coord: any, index: number) =>
        Math.round(Number(coord) * (index % 2 === 0 ? widthRatio : heightRatio))
      );
      area.setAttribute('coords', scaledCoords.join(','));
    });
  }

  scrollToSection(sectionId: string): void {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const offset = 5 * 16;
      const scrollPosition = rect.top + window.pageYOffset - offset;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }
}
